import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  Box,
  TextField,
  Avatar,
  Typography,
  Paper,
  Divider,
  Button,
  Stack,
  Grid,
} from "@mui/material";

const Profile = () => {
  const [user, setUser] = useState({
    firstname: "",
    lastname: "",
    email: "",
    mobile: "",
  });

  const [isEdit, setIsEdit] = useState(false);

  const token = localStorage.getItem("token");
  const userId = localStorage.getItem("userId");

  // ✅ Fetch User
  const fetchUser = async () => {
    try {
      const res = await axios.get(`/api/user/getuser/${userId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setUser(res.data.getaUser || {});
    } catch (error) {
      console.log(error);
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch user";

      toast.error(message);
    }
  };

  useEffect(() => {
    fetchUser();
  }, []);

  // ✅ Handle Change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setUser({ ...user, [name]: value });
  };

  // ✅ Update User
  const handleUpdate = async () => {
    try {
      const res = await axios.put(`/api/user/update/${userId}`, user, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const updatedUser = res.data;

      localStorage.setItem("firstname", updatedUser.firstname);
      localStorage.setItem("lastname", updatedUser.lastname);
      localStorage.setItem("email", updatedUser.email);
      localStorage.setItem("mobile", updatedUser.mobile);

      window.dispatchEvent(new Event("userUpdated"));

      toast.success("Profile Updated Successfully");
      setIsEdit(false);
      fetchUser();
    } catch (error) {
      console.log(error);
      // ✅ Error message handle
      const message =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Something went wrong";

      toast.error(message);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#eef2f6",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
      }}
    >
      <Paper
        elevation={6}
        sx={{
          width: "100%",
          maxWidth: 420,
          borderRadius: 4,
          overflow: "hidden",
        }}
      >
        {/* Top Banner */}
        <Box
          sx={{
            height: 120,
            background: "linear-gradient(135deg, #1976d2, #42a5f5)",
          }}
        />

        {/* Profile Section */}
        <Box sx={{ textAlign: "center", mt: -6, px: 3 }}>
          <Avatar
            sx={{
              width: 90,
              height: 90,
              margin: "0 auto",
              bgcolor: "#1976d2",
              fontSize: 30,
              border: "4px solid white",
              boxShadow: 3,
            }}
          >
            {user.firstname || user.lastname
              ? `${user.firstname?.charAt(0) || ""}${user.lastname?.charAt(0) || ""}`.toUpperCase()
              : "U"}
          </Avatar>

          <Typography variant="h5" mt={2} fontWeight="600">
            {`${user.firstname || ""} ${user.lastname || ""}`}
          </Typography>

          <Typography variant="body2" color="text.secondary">
            Profile Information
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Form */}
        <Box sx={{ px: 3, pb: 3 }}>
          {/* ✅ Name Fields */}
          {!isEdit ? (
            <TextField
              fullWidth
              label="Full Name"
              value={`${user.firstname || ""} ${user.lastname || ""}`.trim()}
              margin="normal"
              InputProps={{ readOnly: true }}
            />
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="firstname"
                  value={user.firstname || ""}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  name="lastname"
                  value={user.lastname || ""}
                  onChange={handleChange}
                />
              </Grid>
            </Grid>
          )}

          {/* Email */}
          <TextField
            fullWidth
            label="Email Address"
            value={user.email || ""}
            margin="normal"
            InputProps={{ readOnly: true }}
            sx={{
              backgroundColor: "#f5f5f5",
              borderRadius: "8px",
            }}
          />

          {/* Mobile */}
          <TextField
            fullWidth
            label="Mobile Number"
            name="mobile"
            value={user.mobile || ""}
            onChange={handleChange}
            margin="normal"
            InputProps={{ readOnly: !isEdit }}
          />

          {/* Buttons */}
          <Stack direction="row" spacing={2} mt={3}>
            {!isEdit ? (
              <Button
                fullWidth
                variant="contained"
                onClick={() => setIsEdit(true)}
                sx={{
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: "600",
                  background: "linear-gradient(135deg, #1976d2, #42a5f5)",
                }}
              >
                Edit Profile
              </Button>
            ) : (
              <>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleUpdate}
                  sx={{
                    borderRadius: "10px",
                    textTransform: "none",
                    fontWeight: "600",
                    background: "linear-gradient(135deg, #2e7d32, #66bb6a)",
                  }}
                >
                  Save
                </Button>

                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => {
                    setIsEdit(false);
                    fetchUser(); // reset values
                  }}
                  sx={{
                    borderRadius: "10px",
                    textTransform: "none",
                    fontWeight: "600",
                  }}
                >
                  Cancel
                </Button>
              </>
            )}
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
};

export default Profile;
